import { Notice } from "@/ui/Notice";
import { ConflictRecovery } from "@/ui/library/AssetEditor";

import type { NamedDraftEditor } from "./useNamedDraft";

/**
 * The two failure surfaces every named editor shares (#492).
 *
 * Both are #33's, reused rather than reimplemented: a stale save recovers
 * through the same `ConflictRecovery` the generic editor uses, so "publishing on
 * top of it keeps both versions" has exactly one statement in this application,
 * and a refused save shows the service's own sentence.
 *
 * They are components rather than inline conditions because each of the three
 * named surfaces needs both, and because narrowing `failure` once here is what
 * lets the recovery close over a version it has already proven is present.
 */

export function NamedConflictRecovery({
  editor,
  rebaseRefusal = null,
}: {
  editor: NamedDraftEditor;
  rebaseRefusal?: string | null;
}) {
  const failure = editor.failure;
  if (failure === null || failure.kind !== "stale") {
    return null;
  }
  return (
    <ConflictRecovery
      assetId={editor.assetId}
      version={failure.version}
      onRebase={() => editor.rebase(failure.version)}
      onDiscardMine={(newer) => editor.takeNewer(newer)}
      rebaseRefusal={rebaseRefusal}
    />
  );
}

/**
 * The refusals a Save raised before it left the browser.
 *
 * Needed wherever Save can be pressed, which is NOT only the document editor:
 * the Main-concern detail publishes from its row list too, and a draft can be
 * unsendable for a reason that belongs to a document not currently open — an
 * empty prompt, a part over the size ceiling. Without this the Save would set
 * problems nobody rendered, and the button would appear to do nothing.
 */
export function NamedProblems({ editor }: { editor: NamedDraftEditor }) {
  if (editor.problems.length === 0) {
    return null;
  }
  return (
    // The live region is the WRAPPER, not each item. A `role` on an `<li>`
    // replaces its implicit `listitem`, which leaves the `<ul>` holding children
    // that are not list items — axe's `list` rule, and a screen reader that stops
    // announcing "list of 2" with it. Announcing the group once is also the
    // better behaviour: a Save refused for three reasons used to fire three
    // assertive interruptions, which is the opposite of what this surface is for.
    <div role="alert">
      <ul className="space-y-1">
        {editor.problems.map((problem, position) => (
          // THE POSITION IS WHAT MAKES THIS KEY UNIQUE, and this is the list
          // where that matters most: unlike the two that filter to
          // `index === null`, this one renders EVERY refusal, including one per
          // offending part. `findPartProblems` legitimately returns the same
          // sentence twice whenever two parts fail the same way — clearing both
          // language boxes of a bilingual main concern refuses each empty prompt
          // with "A prompt part cannot be empty." — so keyed on the message
          // alone React was handed two children with the same key and asked to
          // reconcile two distinct refusals as one.
          //
          // `${problem.index}:${problem.message}` would be unique for an array
          // `findPartProblems` built. It is not unique for one a caller
          // assembled, and `PartsEditor` renders exactly that: a server refusal
          // fanned across positions parsed back out of its own message. One rule
          // for all four message lists, unique AT THE RENDER SITE rather than by
          // provenance. The attribution stays in the key so it still names the
          // refusal it belongs to in a DevTools tree or a React warning.
          <li
            key={`${position}:${problem.index}:${problem.message}`}
            className="text-sm text-destructive"
          >
            {problem.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NamedRefusal({ editor }: { editor: NamedDraftEditor }) {
  const failure = editor.failure;
  if (failure === null || failure.kind === "stale") {
    return null;
  }
  // #527's shared primitive rather than the container recipe typed out again:
  // a refused publish has taken something away from the person who pressed Save,
  // which is exactly the `destructive` + `role="alert"` pairing it defines.
  return (
    <Notice tone="destructive" role="alert">
      {failure.message}
    </Notice>
  );
}
